/**
 * TrayManager - Manages tray state, keybindings, and configuration
 *
 * Centralizes tray open/close state and provides declarative tray configuration.
 * Each tray type defines its own button badge renderer and content renderer.
 */

import { makeAutoObservable } from "mobx"
import { getTrayConfig, TRAY_CONFIGS } from "../../modules/registry"
import type { TaskModel } from "../TaskModel"
import type { CodeStore } from "../store"
import { preferredPanePresentation, rememberPanePresentation, type PanePresentation, type PaneType } from "./panePresentationPreferences"

export type TrayType = PaneType
export type TrayPresentation = PanePresentation

export class TrayManager {
    openTray: TrayType | null = null
    presentation: TrayPresentation = "modal"

    constructor(
        readonly store: CodeStore,
        readonly taskModel: TaskModel
    ) {
        makeAutoObservable(this, {
            store: false,
            taskModel: false,
        })
    }

    get taskId(): string {
        return this.taskModel.taskId
    }

    get workspaceId(): string {
        return this.taskModel.workspaceId
    }

    toggle(tray: TrayType): void {
        const config = getTrayConfig(tray)
        if (config?.externalPresentation) {
            if (!config.externalPresentation.canOpen(this)) return
            if (config.externalPresentation.isOpen(this)) {
                config.externalPresentation.close(this)
                config.onClose?.(this)
                return
            }
            this.close()
            this.closeExternalPresentations(tray)
            config.externalPresentation.open(this)
            config.onOpen?.(this)
            return
        }

        this.closeExternalPresentations()
        const previousTray = this.openTray
        const closing = previousTray === tray
        this.openTray = closing ? null : tray
        this.presentation = closing ? "modal" : preferredPanePresentation(tray)
        if (previousTray) {
            getTrayConfig(previousTray)?.onClose?.(this)
        }
        if (!closing) {
            console.debug("[TrayManager] toggle: calling onOpen for", tray)
            getTrayConfig(tray)?.onOpen?.(this)
        }
    }

    open(tray: TrayType): void {
        const config = getTrayConfig(tray)
        if (config?.externalPresentation) {
            if (!config.externalPresentation.canOpen(this)) return
            this.close()
            this.closeExternalPresentations(tray)
            config.externalPresentation.open(this)
            config.onOpen?.(this)
            return
        }

        this.closeExternalPresentations()
        const previousTray = this.openTray
        this.openTray = tray
        this.presentation = preferredPanePresentation(tray)
        if (previousTray && previousTray !== tray) {
            getTrayConfig(previousTray)?.onClose?.(this)
        }
        console.debug("[TrayManager] open: calling onOpen for", tray)
        getTrayConfig(tray)?.onOpen?.(this)
    }

    close(): void {
        const previousTray = this.openTray
        this.openTray = null
        this.presentation = "modal"
        if (previousTray) {
            getTrayConfig(previousTray)?.onClose?.(this)
        }
    }

    dock(): void {
        if (!this.openTray) return
        this.presentation = "docked"
        rememberPanePresentation(this.openTray, this.presentation)
    }

    showModal(): void {
        if (!this.openTray) return
        this.presentation = "modal"
        rememberPanePresentation(this.openTray, this.presentation)
    }

    isTrayOpen(tray: TrayType): boolean {
        const externalPresentation = getTrayConfig(tray)?.externalPresentation
        return externalPresentation ? externalPresentation.isOpen(this) : this.openTray === tray
    }

    get isOpen(): boolean {
        return this.openTray !== null
    }

    get isModalOpen(): boolean {
        return this.openTray !== null && this.presentation === "modal"
    }

    get isDocked(): boolean {
        return this.openTray !== null && this.presentation === "docked"
    }

    private closeExternalPresentations(except?: TrayType): void {
        for (const config of TRAY_CONFIGS) {
            if (config.id === except || !config.externalPresentation?.isOpen(this)) continue
            config.externalPresentation.close(this)
            config.onClose?.(this)
        }
    }
}
